import type { MerrRequest } from '@setplex/merr'
import { TError, TInit, TRequest, TResponse } from '@setplex/merr'
import { DEFAULT_HTTP_TIMEOUT, HTTP_LATENCY } from '../index.h'

// constants for mapping durations to colors and back
const MIN = HTTP_LATENCY
const MAX = DEFAULT_HTTP_TIMEOUT - HTTP_LATENCY

/**
 * Extract color from timestamp
 *
 * Color is added as a decimal part of the number,
 * so this function essentially just extract it, e.g.:
 *   1755242959423.1 -> 1
 *   1755242987047.9 -> 9
 */
export function colorFromTimestamp(value: number): number {
  return Number.isInteger(value) ? 0 : Math.round((value % 1) * 10)
}

/**
 * Calculate request weight from its color
 *
 * This function uses a quadratic function to map color to weight:
 *   0 -> 1.00
 *   1 -> 1.04
 *   2 -> 1.15
 *   3 -> 1.33
 *   4 -> 1.59
 *   5 -> 1.93
 *   6 -> 2.33
 *   7 -> 2.81
 *   8 -> 3.37
 *   9 -> 4.00
 *
 * - Fast requests (colors 0-3) have minimal impact
 * - Medium requests (colors 4-6) start having noticeable weight
 * - Slow/failed requests (colors 7-9) have significant impact
 *
 * Function can be changed to adjust the weight distribution.
 */
export function weightFromColor(color: number): number {
  const w = 1 + (1 / 27) * color * color
  return Math.round(w * 100) / 100
}

/**
 * Maps duration to a color:
 *   0 for durations <= min
 *   9 for durations > max
 *   1-9 for durations between min and max, distributed equally across the range
 */
export function colorFromDuration(duration: number): number {
  if (duration <= MIN) return 0
  if (duration > MAX) return 9
  return Math.ceil((duration - MIN) / ((MAX - MIN) / 8))
}

/**
 * Converts a color back to a representative duration in milliseconds.
 * Returns the middle point of the duration range that maps to the given digit.
 */
export function durationFromColor(color: number): number {
  if (color <= 0) return MIN / 2 // middle of 0 to min range
  if (color >= 9) return MAX + MIN // max+min

  const segmentSize = (MAX - MIN) / 8
  const segmentStart = MIN + (color - 1) * segmentSize
  const segmentEnd = MIN + color * segmentSize
  return (segmentStart + segmentEnd) / 2 // middle of the segment
}

/**
 * Get colored timestamp of the request,
 * where color is calculated from the request's timings,
 * and added as a decimal part of the timestamp
 */
export function colored(request: Request | MerrRequest | string): number {
  const now = Date.now()
  const timings =
    request != null && typeof request === 'object' && '__' in request
      ? request.__?.timings
      : undefined

  // if no timings, return current timestamp (color 0)
  if (!timings || !Array.isArray(timings) || timings.length === 0) {
    return now
  }

  // request timings are available, calculate request duration:
  // from latest TResponse|TError to the previous TRequest|TInit
  const responseIdx = timings //
    .findLastIndex((t) => t instanceof TResponse || t instanceof TError)
  const requestIdx = timings
    .slice(0, responseIdx)
    .findLastIndex((t) => t instanceof TRequest || t instanceof TInit)

  // no valid timings found, return current timestamp (color 0)
  if (responseIdx < 0 || requestIdx < 0 || responseIdx <= requestIdx) {
    return now
  }

  // calculate duration and color, which is based on duration
  const duration = Number(timings[responseIdx]) - Number(timings[requestIdx])
  const color = colorFromDuration(duration)

  // add color as a decimal part
  return now + color / 10
}

/**
 * Returns CSS color for the given colored timestamp
 */
// biome-ignore format: this is a debug function
export function debugPrintColor(value: number): string {
  switch (colorFromTimestamp(value)) {
    case 0: return '#808080' // gray
    case 1: return '#8c7575' // gray with red tint
    case 2: return '#986a6a' // light brown-red
    case 3: return '#a45f5f' // medium brown-red
    case 4: return '#b05454' // muted red
    case 5: return '#bc4949' // stronger red
    case 6: return '#c83e3e' // bright red
    case 7: return '#d43333' // vibrant red
    case 8: return '#e02828' // strong red
    case 9: return '#ff0000' // pure red
  }         return '#000000' // should not happen
}
